import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Annotation,
  CodeSnapshot,
  HITL_MESSAGE_MAX_LENGTH,
  HitlMessage,
  createClientMessageId,
  boundCodeSnapshot,
} from '../../services/hitl';
import { useHitlApi } from './HitlApiContext';
import { getSocket } from '../../services/socket';

const HITL_AMBER = '#B8600B';
const HITL_AMBER_LIGHT = '#F5E6D8';
const HITL_VIOLET = '#7C3AED';

interface Props {
  sessionId: string;
  onEnded: (sessionId: string) => void;
  getCodeSnapshot?: () => CodeSnapshot | undefined;
}

export interface PendingHitlMessage {
  content: string;
  clientMessageId: string;
}

export function resolvePendingMessage(
  current: PendingHitlMessage | null,
  content: string,
  createId: () => string = createClientMessageId,
): PendingHitlMessage {
  const normalized = content.trim();
  return current?.content === normalized
    ? current
    : { content: normalized, clientMessageId: createId() };
}

export function mergeMessages(current: HitlMessage[], incoming: HitlMessage): HitlMessage[] {
  const duplicate = current.some(
    (message) =>
      message.id === incoming.id ||
      (incoming.client_message_id &&
        message.client_message_id === incoming.client_message_id) ||
      (message.sequence === incoming.sequence &&
        message.session_id === incoming.session_id),
  );
  if (duplicate) return current;
  return [...current, incoming].sort((a, b) => a.sequence - b.sequence);
}

type JoinAck = { ok?: boolean; error?: string };

export function joinHitlSession(
  socket: ReturnType<typeof getSocket>,
  sessionId: string,
  timeoutMs = 5_000,
): Promise<JoinAck> {
  return new Promise((resolve) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        resolve({ error: 'service_unavailable' });
      }
    }, timeoutMs);
    socket.emit(
      'hitl:session:join',
      { session_id: sessionId },
      (ack: JoinAck) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        resolve(ack);
      },
    );
  });
}

export async function hydrateJoinRehydrate<T>(
  hydrateSnapshot: () => Promise<T | null>,
  isActiveSnapshot: (snapshot: T) => boolean,
  join: () => Promise<JoinAck>,
): Promise<{ snapshot: T | null; ack: JoinAck | null }> {
  const snapshot = await hydrateSnapshot();
  if (!snapshot || !isActiveSnapshot(snapshot)) return { snapshot, ack: null };
  const ack = await join();
  if (ack.ok) await hydrateSnapshot();
  return { snapshot, ack };
}

export function emitIdePeek(
  socket: ReturnType<typeof getSocket>,
  sessionId: string,
  getCodeSnapshot?: () => CodeSnapshot | undefined,
): boolean {
  const snapshot = boundCodeSnapshot(getCodeSnapshot?.());
  if (!snapshot?.file_path || snapshot.content === undefined) return false;
  socket.emit('hitl:session:ide-peek', {
    session_id: sessionId,
    file_path: snapshot.file_path,
    content: snapshot.content,
  });
  return true;
}

const HitlLiveSession: React.FC<Props> = ({
  sessionId,
  onEnded,
  getCodeSnapshot,
}) => {
  const { t } = useTranslation();
  const hitlApi = useHitlApi();
  const [messages, setMessages] = useState<HitlMessage[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [composer, setComposer] = useState('');
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pendingMessageRef = useRef<PendingHitlMessage | null>(null);
  const confirmedClientIdsRef = useRef(new Set<string>());
  const generationRef = useRef(0);

  useEffect(() => {
    const generation = ++generationRef.current;
    let active = true;
    let joining = false;
    let reconcilePromise: ReturnType<typeof hitlApi.getSession> | null = null;
    let pollId: number | undefined;
    const socket = getSocket();
    const isCurrent = () =>
      active && generationRef.current === generation;
    setMessages([]);
    setAnnotations([]);
    setComposer('');
    setSending(false);
    setError(null);
    setConnected(false);
    setLoading(true);
    pendingMessageRef.current = null;
    confirmedClientIdsRef.current.clear();

    const reconcile = () => {
      if (!isCurrent()) return Promise.resolve(null);
      if (reconcilePromise) return reconcilePromise;
      reconcilePromise = hitlApi.getSession(sessionId)
        .then((snapshot) => {
          if (!isCurrent()) return snapshot;
          setMessages(snapshot.messages);
          setAnnotations(snapshot.annotations);
          const pending = pendingMessageRef.current;
          if (
            pending &&
            snapshot.messages.some(
              (message) =>
                message.client_message_id === pending.clientMessageId,
            )
          ) {
            confirmedClientIdsRef.current.add(pending.clientMessageId);
            pendingMessageRef.current = null;
            setComposer((current) =>
              current.trim() === pending.content ? '' : current,
            );
            setError(null);
          }
          if (snapshot.session.status !== 'active' && pollId !== undefined) {
            window.clearInterval(pollId);
            pollId = undefined;
            onEnded(sessionId);
          }
          return snapshot;
        })
        .finally(() => {
          reconcilePromise = null;
        });
      return reconcilePromise;
    };

    const hydrateThenJoin = async () => {
      if (!isCurrent() || joining) return;
      joining = true;
      setConnected(false);
      try {
        if (socket.connected) {
          const { ack } = await hydrateJoinRehydrate(
            reconcile,
            (snapshot) => snapshot.session.status === 'active',
            () =>
              isCurrent()
                ? joinHitlSession(socket, sessionId)
                : Promise.resolve({ error: 'stale_session' }),
          );
          if (!isCurrent()) return;
          if (ack?.ok) {
            setConnected(true);
            setError(null);
            emitIdePeek(socket, sessionId, getCodeSnapshot);
          } else if (ack) {
            setError('The live session could not be joined. Reconciliation will keep retrying.');
          }
        } else {
          await reconcile();
        }
      } catch (err) {
        if (isCurrent()) {
          setError(err instanceof Error ? err.message : 'The live session could not be loaded');
        }
      } finally {
        if (isCurrent()) setLoading(false);
        joining = false;
      }
    };

    const onMessage = (message: HitlMessage) => {
      if (isCurrent() && message.session_id === sessionId) {
        setMessages((current) => mergeMessages(current, message));
        if (
          message.client_message_id &&
          pendingMessageRef.current?.clientMessageId === message.client_message_id
        ) {
          confirmedClientIdsRef.current.add(message.client_message_id);
          const confirmedContent = pendingMessageRef.current.content;
          pendingMessageRef.current = null;
          setComposer((current) =>
            current.trim() === confirmedContent ? '' : current,
          );
          setError(null);
        }
      }
    };
    const onResolved = (payload: { session_id?: string }) => {
      if (isCurrent() && payload.session_id === sessionId) {
        if (pollId !== undefined) window.clearInterval(pollId);
        pollId = undefined;
        onEnded(sessionId);
      }
    };
    const onAnnotation = (annotation: Annotation) => {
      if (!isCurrent() || annotation.session_id !== sessionId) return;
      setAnnotations((current) =>
        current.some((item) => item.id === annotation.id)
          ? current
          : [...current, annotation],
      );
    };
    const onConnect = () => {
      void hydrateThenJoin();
    };
    const onDisconnect = () => {
      if (isCurrent()) setConnected(false);
    };

    socket.on('hitl:session:message', onMessage);
    socket.on('hitl:session:annotation', onAnnotation);
    socket.on('hitl:session:resolved', onResolved);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    pollId = window.setInterval(() => {
      void reconcile().catch((err) => {
        if (isCurrent()) {
          setError(err instanceof Error ? err.message : 'The live session could not be reconciled');
        }
      });
    }, 5_000);
    void hydrateThenJoin();

    return () => {
      active = false;
      if (generationRef.current === generation) {
        generationRef.current += 1;
      }
      if (pollId !== undefined) window.clearInterval(pollId);
      socket.off('hitl:session:message', onMessage);
      socket.off('hitl:session:annotation', onAnnotation);
      socket.off('hitl:session:resolved', onResolved);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.emit('hitl:session:leave', { session_id: sessionId });
    };
  }, [getCodeSnapshot, hitlApi, onEnded, sessionId]);

  const handleSend = async () => {
    const content = composer.trim();
    if (!content || sending) return;
    const generation = generationRef.current;
    const isCurrent = () => generationRef.current === generation;
    const pending = resolvePendingMessage(pendingMessageRef.current, content);
    pendingMessageRef.current = pending;
    setSending(true);
    setError(null);
    try {
      const message = await hitlApi.sendMessage(
        sessionId,
        content,
        pending.clientMessageId,
      );
      if (!isCurrent()) return;
      setMessages((current) => mergeMessages(current, message));
      confirmedClientIdsRef.current.add(pending.clientMessageId);
      if (pendingMessageRef.current?.clientMessageId === pending.clientMessageId) {
        pendingMessageRef.current = null;
      }
      setComposer((current) => (current.trim() === content ? '' : current));
    } catch (err) {
      if (
        isCurrent() &&
        !confirmedClientIdsRef.current.has(pending.clientMessageId)
      ) {
        setError(err instanceof Error ? err.message : 'Your message could not be sent');
      }
    } finally {
      if (isCurrent()) setSending(false);
    }
  };

  return (
    <div
      style={{
        padding: 16,
        background: '#fff',
        border: `1px solid ${HITL_AMBER}`,
        borderRadius: 8,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: '#18181B',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${HITL_AMBER_LIGHT}` }}>
        <span
          aria-hidden
          style={{ width: 8, height: 8, borderRadius: '50%', background: connected ? '#15803D' : '#A1A1AA' }}
        />
        <div style={{ fontWeight: 600 }}>Human instructor session</div>
        <span style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 10, background: HITL_AMBER_LIGHT, color: HITL_AMBER, fontSize: 11, fontWeight: 700 }}>
          HUMAN
        </span>
      </div>

      <div aria-live="polite" style={{ fontSize: 12, color: '#71717A', marginBottom: 8 }}>
        {loading ? 'Loading the durable transcript…' : connected ? 'Live updates connected' : 'Reconnecting live updates…'}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 400, overflowY: 'auto', marginBottom: 12 }}>
        {!loading && messages.length === 0 && (
          <div style={{ color: '#71717A', fontSize: 13 }}>Your instructor has joined. Send the first message when you are ready.</div>
        )}
        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              alignSelf: message.sender_type === 'learner' ? 'flex-end' : 'flex-start',
              maxWidth: '82%',
              padding: 10,
              borderRadius: 8,
              background: message.sender_type === 'instructor' ? HITL_AMBER_LIGHT : message.sender_type === 'ai' ? '#EDE9FE' : '#F4F4F5',
              border: `1px solid ${message.sender_type === 'instructor' ? HITL_AMBER : message.sender_type === 'ai' ? HITL_VIOLET : '#D4D4D8'}`,
              fontSize: 14,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
            }}
          >
            <div style={{ fontSize: 11, color: '#71717A', marginBottom: 4 }}>
              {message.sender_type === 'learner' ? 'You' : message.sender_type === 'instructor' ? 'Instructor' : t('companion.name')}
            </div>
            {message.content}
          </div>
        ))}
        {annotations.map((annotation) => (
          <div key={annotation.id} style={{ padding: 8, marginTop: 4, background: HITL_AMBER_LIGHT, borderLeft: `3px solid ${HITL_AMBER}`, borderRadius: 4, fontSize: 13 }}>
            <div style={{ color: '#71717A', fontSize: 11 }}>
              Instructor annotation · lines {annotation.line_range_start}–{annotation.line_range_end}
              {annotation.file_path ? ` in ${annotation.file_path}` : ''}
            </div>
            {annotation.note}
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <textarea
          value={composer}
          onChange={(event) => {
            const next = event.target.value;
            if (
              pendingMessageRef.current &&
              pendingMessageRef.current.content !== next.trim()
            ) {
              pendingMessageRef.current = null;
            }
            setComposer(next);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void handleSend();
          }}
          maxLength={HITL_MESSAGE_MAX_LENGTH}
          disabled={loading || sending}
          aria-label="Message to instructor"
          placeholder="Reply to your instructor…"
          rows={2}
          style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #D4D4D8', fontFamily: 'inherit', fontSize: 14, resize: 'vertical' }}
        />
        <button
          type="button"
          onClick={() => void handleSend()}
          disabled={loading || sending || !composer.trim()}
          style={{
            minHeight: 44,
            padding: '10px 16px',
            borderRadius: 6,
            border: 0,
            background: HITL_AMBER,
            color: '#fff',
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: loading || sending || !composer.trim() ? 'not-allowed' : 'pointer',
            opacity: loading || sending || !composer.trim() ? 0.55 : 1,
          }}
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </div>
      <div style={{ fontSize: 11, color: '#71717A', marginTop: 4 }}>
        Cmd/Ctrl+Enter to send · {composer.length}/{HITL_MESSAGE_MAX_LENGTH}
      </div>
      {error && <div role="alert" style={{ marginTop: 8, color: '#B91C1C', fontSize: 12 }}>{error}</div>}
    </div>
  );
};

export default HitlLiveSession;
